/**
 * HomeRoute - Smart home page routing with Intent Detection
 * 
 * Shows Landing page for non-authenticated users
 * Shows SpacesPage (journals) for authenticated users
 * 
 * INTENT DETECTION: If user signed up via "Create journal" CTA,
 * automatically creates a journal and navigates them into it
 */

import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { Navigate, useNavigate } from 'react-router-dom';
import Landing from '@/pages/Landing';
import SpacesPage from '@/pages/SpacesPage';
import InactiveStateHandler from '@/components/subscription/InactiveStateHandler';
import { createJournal } from '@/services/journalService';
import { toast } from 'sonner';

const HomeRoute = () => {
    const { user, loading } = useAuth();
    const { subscription, loading: subscriptionLoading } = useSubscription();
    const navigate = useNavigate();
    const [isCreatingJournal, setIsCreatingJournal] = useState(false);

    // Check for pending action (intent detection) after auth
    useEffect(() => {
        const handlePendingAction = async () => {
            const pendingAction = localStorage.getItem('pendingAction');

            if (pendingAction === 'create_journal' && user && subscription && !isCreatingJournal) {
                setIsCreatingJournal(true);
                localStorage.removeItem('pendingAction');

                try {
                    // Auto-create journal for the user
                    const newJournal = await createJournal(user.id, {
                        name: 'Untitled Journal',
                    });

                    toast.success('Welcome to StudiBudi! 🎉', {
                        description: 'Your first learning journal is ready.',
                    });

                    // Navigate directly into the new journal with auto-connect flag
                    // This will trigger the voice tutor to connect automatically
                    navigate(`/journals/${newJournal.id}?autoConnect=true`, { replace: true });
                } catch (error) {
                    console.error('Failed to auto-create journal:', error);
                    toast.error('Welcome! Head to your journals to get started.');
                    setIsCreatingJournal(false);
                }
            }
        };

        handlePendingAction();
    }, [user, subscription, navigate, isCreatingJournal]);

    // Show loading spinner while checking auth state
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F8F8F6]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
        );
    }

    // Not logged in -> show Landing page
    if (!user) {
        return <Landing />;
    }

    // Still loading subscription after auth confirmed
    if (subscriptionLoading || isCreatingJournal) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-[#F8F8F6] gap-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                {isCreatingJournal && (
                    <p className="text-sm text-slate-500">Creating your journal...</p>
                )}
            </div>
        );
    }

    // Logged in but no subscription -> redirect to welcome video for trial activation
    if (!subscription) {
        return <Navigate to="/welcome-video" replace />;
    }

    // Logged in with subscription -> show SpacesPage (journals)
    return (
        <InactiveStateHandler>
            <SpacesPage />
        </InactiveStateHandler>
    );
};

export default HomeRoute;
